import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface MovieModalContextType {
  movieId: number | null;
  openMovie: (id: number) => void;
  closeMovie: () => void;
  // Notify when a movie's data is updated (e.g., rating, watchlist)
  notifyMovieUpdated: (movie: any) => void;
  // Subscribe to movie updates; returns unsubscribe function
  onMovieUpdate: (callback: (movie: any) => void) => () => void;
}

const MovieModalContext = createContext<MovieModalContextType>({
  movieId: null,
  openMovie: () => {},
  closeMovie: () => {},
  notifyMovieUpdated: () => {},
  onMovieUpdate: () => () => {},
});

export function MovieModalProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [movieId, setMovieId] = useState<number | null>(null);
  const [updateListeners, setUpdateListeners] = useState<
    Set<(movie: any) => void>
  >(new Set());

  const openMovie = useCallback((id: number) => setMovieId(id), []);
  const closeMovie = useCallback(() => setMovieId(null), []);

  const notifyMovieUpdated = useCallback((movie: any) => {
    setUpdateListeners((prevListeners) => {
      prevListeners.forEach((listener) => listener(movie));
      return prevListeners;
    });
  }, []);

  const onMovieUpdate = useCallback((callback: (movie: any) => void) => {
    setUpdateListeners((prev) => new Set(prev).add(callback));
    // Return unsubscribe function
    return () => {
      setUpdateListeners((prev) => {
        const next = new Set(prev);
        next.delete(callback);
        return next;
      });
    };
  }, []);

  const value = useMemo(
    () => ({
      movieId,
      openMovie,
      closeMovie,
      notifyMovieUpdated,
      onMovieUpdate,
    }),
    [movieId, openMovie, closeMovie, notifyMovieUpdated, onMovieUpdate],
  );

  return (
    <MovieModalContext.Provider value={value}>
      {children}
    </MovieModalContext.Provider>
  );
}

export const useMovieModal = () => useContext(MovieModalContext);
